/* eslint-disable react/function-component-definition, react/destructuring-assignment */
import clsx from 'clsx'
import React, { useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FILTER_TYPES } from 'react-bootstrap-table2-filter'
import { Skeleton } from '../Skeleton'
import { createFilter, createFilterRenderer } from './filters'
import Menu from './Menu'
import './Table.module.scss'

const headerTooltip = ({
  id = 'table-header-tooltip',
  message,
  icon,
  className,
  placement,
  ariaLabel = 'More information',
}) => (
  <>
    &nbsp;
    <OverlayTrigger
      placement={placement || 'top'}
      delay={{ show: 250, hide: 400 }}
      overlay={(tooltipProps) => (
        <Tooltip id={id} {...tooltipProps} className={className}>
          {message}
        </Tooltip>
      )}
    >
      <span
        className={clsx('Uic-Table-headerTooltipTrigger', 'Uic-Table-pointerEventsAuto', 'Uic-Table-cursorAuto')}
        tabIndex={0}
        role="button"
        aria-label={typeof message === 'string' ? message : ariaLabel}
      >
        <i className={icon || 'fas fa-circle-info'} aria-hidden="true" />
      </span>
    </OverlayTrigger>
  </>
)

const headerFormatter =
  (loading) =>
  (column, colIndex, { sortElement, filterElement }) => (
    <div className="Uic-Table-headerCell">
      <div
        title={column.showTitle ? column.text : undefined}
        className={clsx(
          'Uic-Table-headerLabel',
          !filterElement && 'Uic-Table-headerText', // plain title (wraps before the table scrolls)
          column?.filter?.props?.type === FILTER_TYPES.MULTISELECT && 'Uic-Table-multiselect', // multiselect
          !loading && (filterElement || column.showTitle) && 'Uic-Table-pointerEventsAuto',
        )}
      >
        {filterElement || column.text}
      </div>
      {column.tooltip ? headerTooltip(column.tooltip) : null}
      {sortElement && (
        <div className={clsx('Uic-Table-headerCaret', 'Uic-Table-noWrap', !loading && 'Uic-Table-pointerEventsAuto')}>
          {sortElement}
        </div>
      )}
    </div>
  )

const loadingFormatter = () => <Skeleton />

const createMenuColumn = (menu, loading, showMenu) => ({
  dataField: '',
  text: '',
  formatter: (cell, row, rowIndex, extraData) => (
    <Menu options={extraData.menu} row={row} loading={extraData.loading} showMenu={extraData.showMenu} />
  ),
  formatExtraData: { menu, loading, showMenu },
  headerClasses: 'Uic-Table-menuCell',
})

const useConfiguredColumns = ({ columns, loading, readOnly, menu, showMenu, filtersOnChange }) => {
  const dataColumns = useMemo(
    () =>
      columns.map(({ filter, filterRenderer, ...column }) => {
        if (filter) {
          const registerFilterOnChange = (onChange) => {
            // eslint-disable-next-line no-param-reassign
            filtersOnChange[column.dataField] = onChange
          }

          // eslint-disable-next-line no-param-reassign
          filter.props = {
            ...filter.props,
            getFilter: registerFilterOnChange,
          }
        }
        return {
          headerFormatter: headerFormatter(loading),
          filter: createFilter(filter, column.filterValue),
          filterRenderer: createFilterRenderer(filter, filterRenderer),
          ...column,
          formatter: loading ? loadingFormatter : column.formatter,
          formatExtraData: loading ? { loading } : column.formatExtraData,
        }
      }),
    [columns, loading, filtersOnChange],
  )

  const configuredColumns = useMemo(
    () => dataColumns.concat(!readOnly && menu ? createMenuColumn(menu, loading, showMenu) : []),
    [dataColumns, loading, menu, readOnly, showMenu],
  )

  return configuredColumns
}

export default useConfiguredColumns
